import { ref } from "vue";
import Button from "../../../../../core/button";
import { replaceDefaultVNode } from "../../../../../core/vueHelpers";
import { VueBrowseClasses } from "../../../classes";
import { BrowseStoreState } from "../../../store/types";
import * as ScopedSlots from "../../default/scopedSlots";
import { Classes, getButton } from "../common";
import { conditionalField, getApplyButton, getFavoriteButtons, noRows, redirectToViewAll } from "../listBody";
import { JobPosting, JobPostingState, JobPostingStore } from "../types";

export default function setup(dataSource: JobPostingState, state: BrowseStoreState<JobPostingStore, JobPosting>, slots: Readonly<any>): () => JSX.Element
{
    const getRows = (): JSX.Element[] =>
    {
        if (state.displayRows.length === 0)
        {
            return [noRows(state.displayColumns.length)];
        }

        let rows: JSX.Element[] = [];

        for (const displayRow of state.displayRows)
        {
            const jobPosting = displayRow.rowData;
            const jobPostingID = jobPosting.JobPostingID;

            let detailsRef = ref<HTMLElement>();
            let expandButtonRef = ref<typeof Button>();
            let collapseButtonRef = ref<typeof Button>();

            const shareButtonClick = () =>
            {
                return window.skyward.modalLayer.showDialog('Job Posting Link', () => $.ajax(window.skyward.global.virtualDir + `ApplicantTracking/JobBoard/GetJobPostingLinkPopup/${jobPostingID}?renderType=contentOnly`,
                    {
                        type: 'POST',
                        dataType: 'HTML'
                    }), ['close'], { id: "JobPostingShareModal" });
            }
            const expandButtonClick = () =>
            {
                detailsRef.value?.classList.remove(Classes.DisplayNone);
                expandButtonRef.value?.classList.add(Classes.DisplayNone);
                collapseButtonRef.value?.classList.remove(Classes.DisplayNone);
            }
            const collapseButtonClick = () =>
            {
                detailsRef.value?.classList.add(Classes.DisplayNone);
                expandButtonRef.value?.classList.remove(Classes.DisplayNone);
                collapseButtonRef.value?.classList.add(Classes.DisplayNone);
            }
            const attachmentsButtonClick = () =>
            {
                window.skyward.modalLayer.showDialog("Attachments", () => $.ajax(window.skyward.global.virtualDir + `ApplicantTracking/JobPosting/GetAttachments/${jobPostingID}`), ['close'], { id: "JobPostingAttachmentModal" });
            }

            rows.push(<tr
                ref={(ref: any) => { state.addRowRef(displayRow.rowID, ref as Element) }}
                key={displayRow.rowID}
                class={{
                    [VueBrowseClasses.BodyRow]: true,
                    [VueBrowseClasses.BodyRowSelected]: displayRow.rowID == state.selectedRowID
                }}
                aria-selected={displayRow.rowID == state.selectedRowID}
                onClick={(_event: MouseEvent) => { state.selectRow(displayRow.rowID, true); }}
            >
                {
                    displayRow.cells.map(cell =>
                    {
                        const columnWidth = cell.column.width <= 0 ? "auto" : cell.column.width + "px";
                        let columnWrap: "wrap" | "nowrap" = "wrap";
                        let contents: JSX.Element[] = [];

                        switch (cell.column.fieldName as keyof JobPosting | "")
                        {
                            case '': //View More
                                columnWrap = "nowrap";
                                contents =
                                    [
                                    getButton("Details", "Expand", expandButtonClick, { reference: expandButtonRef }),
                                    getButton("Details", "Collapse", collapseButtonClick, { reference: collapseButtonRef, visible: false })
                                    ];
                                break;
                            case 'JobTitle':
                                const jobTitle = (<span class="underline jobTitle" onClick={() => { if (!dataSource.isPreview) redirectToViewAll(jobPostingID) }}>{jobPosting.JobTitle}</span>);
                                    contents = [jobPosting.IsFeatured
                                        ? (<div class="featuredJobTitleWrapper">
                                              {jobTitle}
                                              <span class="featuredJobPostingText">Featured</span>
                                          </div>)
                                        : jobTitle];
                                break;
                            case 'IsFavoriteJobPosting':
                                contents = getFavoriteButtons(dataSource.applicantID,
                                    jobPostingID,
                                    jobPosting.IsFavoriteJobPosting,
                                    dataSource.isPreview);
                                break;
                            case 'ApplicationStatus':
                                columnWrap = "nowrap";
                                contents = [getApplyButton(jobPostingID, jobPosting.ApplicationStatus, dataSource.isPreview)];
                                break;
                            default:
                                contents = [replaceDefaultVNode(slots[ScopedSlots.bodyCellContent]?.({ cell: cell })) ?? cell.value]
                                break;
                        }

                        return (
                            <td class={VueBrowseClasses.BodyCell} style={{ width: columnWidth, maxWidth: "30rem" }}>
                                <div class={{ [VueBrowseClasses.BodyCellContent]: true, [VueBrowseClasses.BodyCellContentHasButton]: !cell.column.displayName }}
                                    style={{ width: columnWidth, "white-space": columnWrap }}>
                                    {contents}
                                </div>
                            </td>
                        );
                    })
                }
            </tr>);

            const upperDetailsFields = (): (JSX.Element | null)[] => Object.keys(dataSource.detailFieldPathsByDisplayString)
                .map(detailDisplayName => conditionalField(`${detailDisplayName.capitalize()}: `, jobPosting[dataSource.detailFieldPathsByDisplayString[detailDisplayName]]));

            const numberOfAttachments = jobPosting.ExternalAttachmentCount;
            const showInternal = dataSource.applicantInternalDistrictIDs?.includes(jobPosting.DistrictID) == true;
            rows.push(<tr>
                <td ref={detailsRef} class={`jobPostingDetails ${Classes.DisplayNone}`} colspan={state.displayColumns.length}>
                    <div class="sectionHeader jobPostingSectionHeader">
                        <h2 class="sectionHeader__text jobPostingSectionHeaderText">Job Details</h2>
                    </div>
                    <div class="jobPostingDetailsContent">
                        <div>
                            {upperDetailsFields()}
                            {conditionalField("Internal Posted Date: ", jobPosting.AcceptingInternalApplicationsStartDate, showInternal)}
                            {conditionalField("Internal Deadline: ", jobPosting.AcceptingInternalApplicationsEndDate, showInternal)}
                            {conditionalField(null, jobPosting.Summary)}
                        </div>
                        <div class="jobPostingDetailsButtonsContainer">
                            {getButton(`Attachments (${numberOfAttachments})`, 'Attachments', attachmentsButtonClick, {visible: numberOfAttachments > 0, disabled: dataSource.isPreview})}
                            {getButton('View Full Job Description', 'View', () => { redirectToViewAll(jobPostingID) }, {disabled: dataSource.isPreview, spanClass: "underline"})}
                            {getButton('Share', 'Share', shareButtonClick, {disabled: dataSource.isPreview})}
                        </div>
                    </div>
                </td>
            </tr>);

            // An extra row to make the striped row styling works
            rows.push(<tr></tr>);
        }
        return rows;
    }
    return () => (
        <tbody class={`jobPostingsDesktop ${VueBrowseClasses.TBody}`}>
            {getRows()}
        </tbody>
    );
}
